import { DataCard, IntegrationBanner } from '@fluentsms/agentnet-web-components';
import { Box, createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { MarketplaceProduct } from 'api/admin/types';
import { RenderContent } from 'features/help/components/RenderContent/RenderContent';
import { disableIntegrationText, EULA_Texts } from 'features/help/constants';
import { useMessagingQuery } from 'features/help/queries/useMessagingQuery';
import { Message } from 'features/help/types/messagingCollection';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import ContactUsDialog from 'ui-kit/components/modal/ContactUsDialog';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { FRAUD_VALIDATION, FRAUD_VALIDATION_READ_ONLY } from 'utilities/constants/activity-rights';
import { normalizeServiceName } from 'utilities/utilities';

interface ServiceDetailsProps {
  goBackAction?: () => void;
  selectedService: MarketplaceProduct;
  refreshServiceList: () => Promise<void>;
  ServiceIcon: JSX.Element;
  allDescriptions: Record<string, Message | null>;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    verticalSpacing: {
      marginBottom: theme.spacing(3),
    },
    linkText: {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      display: 'block',
      marginTop: theme.spacing(1),
      '&:hover': {
        color: theme.palette.secondary.main,
      },
    },
    dataCard: {
      height: 'fit-content',
    },
  }),
);

const ServiceDetails: FC<ServiceDetailsProps> = ({
  selectedService,
  goBackAction,
  refreshServiceList,
  ServiceIcon,
  allDescriptions,
}) => {
  const classes = useStyles();
  const { addSnackbarMessage } = useSnackBars();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { productName, description, isEnabled, category, productCode } = selectedService;
  const [openServiceModal, setOpenServiceModal] = useState<boolean>(false);
  const [contactUsModal, setContactUsModal] = useState<boolean>(false);
  const [showDialogActions, setShowDialogActions] = useState<boolean>(true);
  const { updateMarketPlaceServiceStatus, updateServiceStatus } = useMarketPlace();
  const [acknowledgementMessage, setAcknowledgementMessage] = useState<Message | null>();
  const [descriptionMessage, setDescriptionMessage] = useState<Message | null>();

  const { profile } = profileCtx;

  const productStatus = {
    productCode: productCode,
    isEnabled: !isEnabled,
    isEulaAccepted: !isEnabled,
  };
  const handleCloseDialog = () => {
    setOpenServiceModal(false);
    setShowDialogActions(true);
  };
  const eulaText = isEnabled
    ? disableIntegrationText[productCode]
    : EULA_Texts[productName]?.replaceAll('serviceName', `<strong>${productName}</strong>`);

  useEffect(() => {
    if (updateServiceStatus === 'success') {
      addSnackbarMessage({
        message: `${productName} is ${!isEnabled ? 'enabled' : 'disabled'}`,
        type: 'success',
      });
    }
  }, [updateServiceStatus]);

  const hasFraudValidation = useMemo(() => {
    return profile?.activityRights?.some((rights) => rights.ActivityRightId === FRAUD_VALIDATION);
  }, [profile?.activityRights]);

  const hasReadOnlyFraudValidation = useMemo(() => {
    return profile?.activityRights?.some((rights) => rights.ActivityRightId === FRAUD_VALIDATION_READ_ONLY);
  }, [profile?.activityRights]);

  useEffect(() => {
    const fetchMessages = async () => {
      setAcknowledgementMessage(null);
      setDescriptionMessage(null);

      const ackSlug = 'enhanced-id-validation-acknowledgment';
      const serviceSlug = `feature-${productName.toLowerCase().replace(/\s+/g, '-')}`;

      const [ack, desc] = await Promise.all([useMessagingQuery(ackSlug), useMessagingQuery(serviceSlug)]);

      setAcknowledgementMessage(ack.data);
      setDescriptionMessage(desc.data);
    };

    fetchMessages();
  }, [productName]);

  const testIdPrefixBanner = `${normalizeServiceName(productName)}-button-${isEnabled ? 'disable' : 'enable'}`;
  const testIdPrefixDialog = `${normalizeServiceName(productName)}-${isEnabled ? 'disable' : 'enable'}-button-`;

  return (
    <>
      <AgentNetButton
        variant="text"
        size="small"
        color="primary"
        startIcon={<ArrowBack />}
        onClick={goBackAction && goBackAction}
        data-testid="PaySumBack"
        className={classes.verticalSpacing}
      >
        Add-Ons
      </AgentNetButton>

      <div className={classes.verticalSpacing} data-testid={testIdPrefixBanner}>
        <IntegrationBanner
          title={productName.includes('Enhanced') ? 'Identity Verification' : productName}
          description={description}
          icon={ServiceIcon}
          category={category}
          enabled={isEnabled}
          disabled={!hasFraudValidation && hasReadOnlyFraudValidation}
          onClick={() => setOpenServiceModal(true)}
        />
      </div>

      <DataCard heading="Details" className={classes.dataCard}>
        <Box p={3}>
          <Grid container spacing={3}>
            <Grid item sm={9}>
              {descriptionMessage ? (
                <div className={classes.verticalSpacing}>
                  <RenderContent document={descriptionMessage.messageText} />
                </div>
              ) : (
                <Typography variant="body1">
                  First American Title agents can now seamlessly share data between AgentNet® Production and AgentNet®
                  Services, streamlining the order and delivery of production services.
                </Typography>
              )}
            </Grid>
            <Grid item sm={3}>
              <Box mb={2}>
                <Typography variant="h4" gutterBottom>
                  Support
                </Typography>
                <Typography
                  variant="body1"
                  onClick={() => setContactUsModal(true)}
                  className={classes.linkText}
                  data-testid="add-ons-link-agentnet-customer-support"
                >
                  AgentNet<sup>&reg;</sup> Customer Support
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="h4" gutterBottom>
                  Categories
                </Typography>
                <Typography variant="body1">{selectedService.category}</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="h4" gutterBottom>
                  Legal
                </Typography>
                <Typography
                  variant="body1"
                  component="a"
                  href="https://www.firstam.com/privacy-policy/"
                  target="_blank"
                  rel="noreferrer"
                  className={classes.linkText}
                  data-testid="add-ons-link-privacy-policy"
                >
                  Privacy Policy
                </Typography>
                {isEnabled ? (
                  <Typography
                    variant="body1"
                    data-testid={`${normalizeServiceName(productName)}-link-acknowledgement`}
                    className={classes.linkText}
                    onClick={() => {
                      setOpenServiceModal(true);
                      setShowDialogActions(false);
                    }}
                  >
                    Acknowledgement
                  </Typography>
                ) : null}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DataCard>

      {acknowledgementMessage && (
        <AgentNetConfirmationDialog
          showDialogActions={showDialogActions}
          updatedQAPrefix={{
            close: `${testIdPrefixDialog}close`,
            cancel: `${testIdPrefixDialog}${isEnabled ? 'cancel' : 'decline'}`,
            submit: `${testIdPrefixDialog}${isEnabled ? 'yes-disable' : 'accept'}`,
          }}
          onConfirm={async () => {
            await updateMarketPlaceServiceStatus(productStatus).then(refreshServiceList);
            handleCloseDialog();
          }}
          disablePrimaryBtn={updateServiceStatus === 'pending'}
          open={openServiceModal}
          onDismissAction={handleCloseDialog}
          dialogTitle={isEnabled && showDialogActions ? 'Disable Feature?' : 'Acknowledgement'}
          dialogBtnContent={isEnabled ? 'Yes, Disable' : 'Accept'}
          dialogCancelBtnContent={isEnabled ? 'Cancel' : 'Decline'}
          dialogTextHTML={
            isEnabled && showDialogActions ? (
              <Typography variant="body1" dangerouslySetInnerHTML={{ __html: eulaText }} />
            ) : (
              <RenderContent document={acknowledgementMessage.messageText} />
            )
          }
        />
      )}
      <ContactUsDialog open={contactUsModal} onClose={() => setContactUsModal(false)} />
    </>
  );
};

export default ServiceDetails;
